/**
 * Stock Counts report (WH-RESTOCK, Seth "Proceed" 2026-09-29) — the eyes on
 * the cycle-count history the old Drive workbooks never kept. Two lenses:
 *
 *  - Count Log — every saved count: when, who typed it, whose list (Warehouse
 *    shelf or a person's truck), what it was vs what they found.
 *  - Drift by Item — the shrinkage view: net found-minus-expected per part
 *    over the period, most negative first. A part that keeps coming up short
 *    is walking off, being used untagged on jobs, or mis-received.
 *
 * Data: pegasus_stock_count (kept forever; removing a person never deletes
 * their history). "First count" rows have no previous number, so they carry
 * no drift — honest by design.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, Download, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";

interface CountRow {
  id: string;
  item_id: string;
  part_number: string | null;
  counted_qty: number;
  previous_qty: number | null;
  source: string;
  counted_by: string | null;
  owner_empno: string | null;
  owner_label: string | null;
  created_at: string;
}

const RANGES = [
  { days: 30, label: "Last 30 Days" },
  { days: 90, label: "Last 90 Days" },
  { days: 365, label: "Last Year" },
  { days: 0, label: "All Time" },
];

const qtyText = (n: number) => (Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const who = (email: string | null) => (email ? email.split("@")[0] : "—");

function useStockCounts(days: number) {
  return useQuery({
    queryKey: ["stock_counts_report", days],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ rows: CountRow[]; desc: Map<string, string> }> => {
      let q = supabase.from("pegasus_stock_count").select("*").order("created_at", { ascending: false }).limit(2000);
      if (days > 0) q = q.gte("created_at", new Date(Date.now() - days * 864e5).toISOString());
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as CountRow[];
      const ids = [...new Set(rows.map((r) => r.item_id))];
      const desc = new Map<string, string>();
      for (let i = 0; i < ids.length; i += 200) {
        const { data: items } = await supabase.from("item").select("id, description").in("id", ids.slice(i, i + 200));
        for (const it of (items ?? []) as { id: string; description: string | null }[]) {
          if (it.description) desc.set(it.id, it.description);
        }
      }
      return { rows, desc };
    },
  });
}

function DriftBadge({ d }: { d: number | null }) {
  if (d == null) return <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[11.5px] font-bold text-amber-700">first</span>;
  if (d === 0) return <span className="text-[12.5px] font-semibold text-slate-400">—</span>;
  return (
    <span className={cn(
      "rounded-md px-1.5 py-0.5 text-[12px] font-bold tabular-nums",
      d < 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700",
    )}>
      {d > 0 ? "+" : ""}{qtyText(d)}
    </span>
  );
}

export function StockCountsReport() {
  const [days, setDays] = useState(90);
  const [owner, setOwner] = useState<string>("all");
  const [lens, setLens] = useState<"log" | "drift">("log");
  const [q, setQ] = useState("");
  const { data, isLoading } = useStockCounts(days);

  const rows = data?.rows ?? [];
  const desc = data?.desc ?? new Map<string, string>();
  const listName = (r: CountRow) => r.owner_label ?? "Warehouse";
  const drift = (r: CountRow) => (r.previous_qty == null ? null : Number(r.counted_qty) - Number(r.previous_qty));

  const owners = useMemo(() => {
    const s = new Map<string, string>();
    for (const r of rows) s.set(r.owner_empno ?? "WAREHOUSE", listName(r));
    return [...s.entries()].map(([k, label]) => ({ k, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (owner !== "all" && (r.owner_empno ?? "WAREHOUSE") !== owner) return false;
      if (!s) return true;
      return `${r.part_number ?? ""} ${desc.get(r.item_id) ?? ""} ${who(r.counted_by)} ${listName(r)}`.toLowerCase().includes(s);
    });
  }, [rows, owner, q, desc]);

  const tiles = useMemo(() => {
    const items = new Set(filtered.map((r) => r.item_id));
    const people = new Set(filtered.map((r) => who(r.counted_by)));
    const net = filtered.reduce((sum, r) => sum + (drift(r) ?? 0), 0);
    return { counts: filtered.length, items: items.size, people: people.size, net };
  }, [filtered]);

  const byItem = useMemo(() => {
    const m = new Map<string, { part: string; description: string; counts: number; net: number; last: string; lists: Set<string> }>();
    for (const r of filtered) {
      const key = r.item_id;
      const cur = m.get(key) ?? {
        part: r.part_number ?? "—",
        description: desc.get(r.item_id) ?? r.part_number ?? "—",
        counts: 0, net: 0, last: r.created_at, lists: new Set<string>(),
      };
      cur.counts += 1;
      cur.net += drift(r) ?? 0;
      if (r.created_at > cur.last) cur.last = r.created_at;
      cur.lists.add(listName(r));
      m.set(key, cur);
    }
    return [...m.values()].sort((a, b) => a.net - b.net || b.counts - a.counts);
  }, [filtered, desc]);

  function exportCsv() {
    const esc = (v: string | number | null) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    let csv: string;
    if (lens === "log") {
      csv = ["When,List,Counted By,Part,Description,Was,Counted,Drift",
        ...filtered.map((r) => [
          new Date(r.created_at).toLocaleString(), listName(r), who(r.counted_by),
          r.part_number ?? "", desc.get(r.item_id) ?? "",
          r.previous_qty ?? "", r.counted_qty, drift(r) ?? "",
        ].map(esc).join(","))].join("\n");
    } else {
      csv = ["Part,Description,Counts,Net Drift,Lists,Last Counted",
        ...byItem.map((i) => [
          i.part, i.description, i.counts, i.net, [...i.lists].join(" / "), new Date(i.last).toLocaleDateString(),
        ].map(esc).join(","))].join("\n");
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = lens === "log" ? "stock-counts.csv" : "stock-count-drift.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const sel = "rounded-lg border border-hairline bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-700";

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
          {([["log", "Count Log"], ["drift", "Drift by Item"]] as const).map(([k, label]) => (
            <button key={k} onClick={() => setLens(k)}
              className={cn("rounded-md px-3 py-1.5 text-sm font-semibold transition", lens === k ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700")}>
              {label}
            </button>
          ))}
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={sel}>
          {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
        </select>
        <select value={owner} onChange={(e) => setOwner(e.target.value)} className={sel}>
          <option value="all">All Lists</option>
          {owners.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
        </select>
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part, person, list…"
            className="w-full rounded-lg border border-hairline py-1.5 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </div>
        <div className="ml-auto">
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: "Counts Saved", val: String(tiles.counts) },
          { label: "Items Covered", val: String(tiles.items) },
          { label: "People Counting", val: String(tiles.people) },
          { label: "Net Drift", val: (tiles.net > 0 ? "+" : "") + qtyText(tiles.net), tone: tiles.net < 0 ? "text-rose-600" : tiles.net > 0 ? "text-emerald-600" : "" },
        ].map((t) => (
          <div key={t.label} className="rounded-xl border border-hairline bg-white px-3.5 py-2.5">
            <div className={cn("text-[22px] font-extrabold tabular-nums tracking-tight", t.tone)}>{t.val}</div>
            <div className="text-[12px] font-semibold text-slate-400">{t.label}</div>
          </div>
        ))}
      </div>

      {isLoading ? (
        <p className="rounded-xl border border-hairline bg-white py-10 text-center text-sm text-slate-400">Loading count history…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-hairline bg-white py-10 text-center">
          <ClipboardCheck className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-400">No counts saved in this period yet — they'll land here as the crew counts.</p>
        </div>
      ) : lens === "log" ? (
        <div className="overflow-x-auto rounded-2xl border border-hairline bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-hairline text-[12px] uppercase tracking-wide text-slate-400">
                <th className="px-3.5 py-2.5 font-bold">When</th>
                <th className="px-3.5 py-2.5 font-bold">List</th>
                <th className="px-3.5 py-2.5 font-bold">Counted By</th>
                <th className="px-3.5 py-2.5 font-bold">Part</th>
                <th className="px-3.5 py-2.5 text-right font-bold">Was</th>
                <th className="px-3.5 py-2.5 text-right font-bold">Counted</th>
                <th className="px-3.5 py-2.5 text-right font-bold">Drift</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-hairline last:border-0 hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3.5 py-2.5 text-slate-500">{when(r.created_at)}</td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 font-semibold text-slate-700">{listName(r)}</td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 text-slate-500">{who(r.counted_by)}</td>
                  <td className="px-3.5 py-2.5">
                    <span className="font-semibold text-slate-800">{r.part_number ?? "—"}</span>
                    {desc.get(r.item_id) && <span className="ml-2 text-slate-400">{desc.get(r.item_id)}</span>}
                  </td>
                  <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-500">{r.previous_qty != null ? qtyText(Number(r.previous_qty)) : "—"}</td>
                  <td className="px-3.5 py-2.5 text-right font-bold tabular-nums">{qtyText(Number(r.counted_qty))}</td>
                  <td className="px-3.5 py-2.5 text-right"><DriftBadge d={drift(r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-hairline bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-hairline text-[12px] uppercase tracking-wide text-slate-400">
                <th className="px-3.5 py-2.5 font-bold">Part</th>
                <th className="px-3.5 py-2.5 text-right font-bold">Counts</th>
                <th className="px-3.5 py-2.5 text-right font-bold">Net Drift</th>
                <th className="px-3.5 py-2.5 font-bold">Lists</th>
                <th className="px-3.5 py-2.5 font-bold">Last Counted</th>
              </tr>
            </thead>
            <tbody>
              {byItem.map((i) => (
                <tr key={`${i.part}|${i.description}`} className="border-b border-hairline last:border-0 hover:bg-slate-50/60">
                  <td className="px-3.5 py-2.5">
                    <span className="font-semibold text-slate-800">{i.part}</span>
                    <span className="ml-2 text-slate-400">{i.description}</span>
                  </td>
                  <td className="px-3.5 py-2.5 text-right tabular-nums text-slate-500">{i.counts}</td>
                  <td className="px-3.5 py-2.5 text-right"><DriftBadge d={i.net} /></td>
                  <td className="px-3.5 py-2.5 text-slate-500">{[...i.lists].join(" / ")}</td>
                  <td className="whitespace-nowrap px-3.5 py-2.5 text-slate-500">{new Date(i.last).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
